import React, { useRef, useEffect } from 'react';
import _ from 'lodash';
import styled from 'styled-components';
import { observer } from 'mobx-react';
import { width } from 'styled-system';
import { Flex, Text } from '@dutchie/common/components/rebass-compat';

import useTranslation from 'hooks/use-translation';

import { Image } from 'shared/components';
import useStores from 'shared/hooks/use-stores';

const getDiscountOrder = ({ taxConfig }) => {
  if (taxConfig?.applyDiscountPreTax || taxConfig?.discountTaxOrder === 'discountsFirst') {
    return 'before';
  }
  return 'after';
};

const getToolTipSegment = (t, country) =>
  country === 'Canada'
    ? t('toolTipSegment.Canada', 'federal compliance regulations')
    : t('toolTipSegment.USA', 'state compliance regulations, and may include both state and local taxes');

function Tax({ onClose, open, salesTaxExists, cannabisTaxExists, bottleDepositTaxExists, receiptLineItems }) {
  const buttonRef = useRef();
  const { Cart } = useStores();
  const { t, Trans } = useTranslation();
  const { order, costBreakdown } = Cart;
  const { dispensary } = order;

  const cannabisTax = _.find(receiptLineItems, { type: `cannabisTax` });
  const salesTax = _.find(receiptLineItems, { type: `salesTax` });

  const taxBreakdown = costBreakdown?.receipt?.details?.taxBreakdown;
  const vapourTax = (taxBreakdown?.vape ?? 0) / 100;
  const vapourTaxExists = !!vapourTax;

  // since vapourTax is a subset of sales tax, sales tax might "exist" but be completely comprised of vape taxed products
  // in this case we will hide the sales tax line and only show the vapour tax line
  const hideSalesTaxLineItem = vapourTaxExists && _.get(salesTax, `value`, 0) === vapourTax;

  const salesTaxDisplayValue = _.get(salesTax, `value`, 0) - vapourTax;
  const bottleDepositTaxCents = _.find(receiptLineItems, { type: `bottleDepositTaxCents` });
  const bottleTaxValue = Number(bottleDepositTaxCents?.value);
  const totalTax = _.get(cannabisTax, `value`, 0) + _.get(salesTax, `value`, 0) + bottleTaxValue;
  const { country } = dispensary.location;
  const tooltipSegment = getToolTipSegment(t, country);

  // direct focus to close button on open
  useEffect(() => {
    if (buttonRef.current) {
      buttonRef.current.focus();
    }
  }, [open]);

  return (
    <TaxModal flexWrap='wrap' open={open}>
      <CloseButton ref={buttonRef} onClick={onClose}>
        <Image src='/icons/x-grey.svg' alt='Close' width='11px' />
      </CloseButton>
      <Text fontSize={16} fontWeight='bold' color='#43474a' mb='17px'>
        {t('taxCopy.taxes', 'Taxes')}
      </Text>
      {bottleDepositTaxExists && (
        <Flex width={1} mb='12px'>
          <TableText width={[3 / 4]}>{t('taxCopy.bottleDeposit', 'Bottle Deposit Tax')}</TableText>
          <TableText width={[1 / 4]} right>
            {bottleDepositTaxCents?.value > 0 ? `$${bottleTaxValue.toFixed(2)}` : t('tax.notIncluded', 'not included')}
          </TableText>
        </Flex>
      )}
      {cannabisTaxExists && (
        <Flex width={1} mb='12px'>
          <TableText width={[3 / 4]}>{t('taxCopy.cannabis', 'Cannabis Tax')}</TableText>
          <TableText width={[1 / 4]} right>
            {cannabisTax?.value > 0 ? `$${cannabisTax.value.toFixed(2)}` : t('tax.notIncluded', 'not included')}
          </TableText>
        </Flex>
      )}
      {salesTaxExists && !hideSalesTaxLineItem && (
        <Flex width={1} mb='12px'>
          <TableText width={[3 / 4]}>{t('taxCopy.sales', 'Sales Tax')}</TableText>
          <TableText width={[1 / 4]} right>
            {salesTaxDisplayValue > 0 ? `$${salesTaxDisplayValue.toFixed(2)}` : t('tax.notIncluded', 'not included')}
          </TableText>
        </Flex>
      )}
      {vapourTaxExists && (
        <Flex width={1} mb='12px'>
          <TableText width={[3 / 4]}>{t('taxCopy.vapour', 'Vapour Tax')}</TableText>
          <TableText width={[1 / 4]} right>
            {vapourTax > 0 ? `$${vapourTax.toFixed(2)}` : t('tax.notIncluded', 'not included')}
          </TableText>
        </Flex>
      )}
      {totalTax > 0 && (
        <Flex width={1} mb='20px'>
          <TableText width={[1 / 2]} fontWeight='bold'>
            {t('taxCopy.totalTaxes', 'Total Taxes')}
          </TableText>
          <TableText width={[1 / 2]} right fontWeight='bold'>
            ${totalTax.toFixed(2)}
          </TableText>
        </Flex>
      )}
      <InfoContainer width={1}>
        <Trans i18nKey='taxCopy.disclaimer'>
          Taxes are set by {dispensary.name} to follow {tooltipSegment}. Discounts for this dispensary are applied{' '}
          {getDiscountOrder(dispensary)} taxes.
        </Trans>
      </InfoContainer>
    </TaxModal>
  );
}

export default observer(Tax);

const TaxModal = styled(({ ...props }) => <Flex {...props} />)`
  position: absolute;
  width: calc(100% - 43px);
  left: 21px;
  padding: 20px;
  border-radius: 7px;
  background-color: white;
  z-index: 2;
  box-shadow: 0 2px 5px 1px rgba(0, 0, 0, 0.06);
  opacity: ${(props) => (props.open ? '1' : '0')};
  visibility: ${(props) => (props.open ? 'visible' : 'hidden')};
  transition: opacity 0.2s;
  white-space: nowrap;
`;

const TableText = styled(Text)`
  font-size: 12px;
  color: #6b6f73;
  text-align: ${(props) => (props.right ? 'right' : 'left')};
  white-space: nowrap;
  ${width}
`;

const InfoContainer = styled(Text)`
  font-size: 11px;
  color: #6b6f73;
  line-height: 1.45;
  padding-top: 20px;
  border-top: 1px solid #e4e8ea;
  white-space: normal;
`;

const CloseButton = styled.button`
  appearance: none;
  border: none;
  padding: 0;
  background-color: transparent;
  position: absolute;
  top: 18px;
  right: 18px;
  cursor: pointer;
`;
